import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Search, Ticket, Hourglass, ShieldCheck, Video, MapPin, ArrowRight, Heart } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import Avatar from "@/components/common/Avatar";
import { useAuth } from "@/contexts/AuthContext";
import {
  listMyLinks, listAppointments, listPublicProfessionals, redeemInviteCode,
  requestLink, getPatientBalance, listPlans, createPlanRequest,
  getAvailableSlots, createBooking, listPatientSessions, getProfessionalProfile,
  listMyPlanRequests, getProfessionalUsesCredits
} from "@/lib/db";
import { formatDate, formatDateTime, formatTime } from "@/lib/format";
import { AppointmentStatusBadge } from "@/components/common/StatusBadge";

export function PatientHome() {
  const { profile } = useAuth();
  const [links, setLinks] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profUsesCredits, setProfUsesCredits] = useState(true);

  const [requests, setRequests] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const load = async () => {
    setLoading(true);
    try {
      const [l, a, r] = await Promise.all([
        listMyLinks(),
        listAppointments({ 
          date_from: new Date().toISOString(),
          patient_id: profile?.id 
        }),
        listMyPlanRequests()
      ]);
      setLinks(l);
      setAppts(a);
      setRequests(r);
      setRefreshKey(prev => prev + 1);

      // Fetch credit setting from the active professional
      const activeLink = l?.find(link => link.status === "active");
      if (activeLink?.professional?.id) {
        getProfessionalUsesCredits(activeLink.professional.id)
          .then(setProfUsesCredits)
          .catch(() => setProfUsesCredits(true));
      }
    } catch (error) {
      console.error("Erro ao carregar dados do paciente:", error);
    } finally { setLoading(false); }
  };

  const handleCancel = async (id) => {
    if (!confirm("Deseja realmente cancelar este agendamento? O crédito será estornado para sua conta.")) return;
    try {
      const { deleteAppointment } = await import("@/lib/db");
      await deleteAppointment(id);
      alert("Agendamento cancelado com sucesso!");
      load();
    } catch (e) {
      alert("Erro ao cancelar: " + e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const activeLinks = links.filter(l => l.status === "active");
  const pendingLinks = links.filter(l => l.status === "pending");
  const noLinkAtAll = links.length === 0;

  return (
    <div className="ds-fade-up">
      <PageHeader
        title="Seu cuidado, organizado."
        description="Acompanhe seu vínculo com o profissional, próximas consultas e histórico."
      />

      {activeLinks.length > 0 && profUsesCredits && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <CreditBalanceCard patientId={profile?.id} clinicId={activeLinks[0]?.clinic_id} professionalId={activeLinks[0]?.professional?.id} refreshKey={refreshKey} />
          <RequestPlanSection 
            professionalId={activeLinks[0]?.professional?.id} 
            clinicId={activeLinks[0]?.clinic_id}
            requests={requests} 
          />
        </section>
      )}

      {activeLinks.length > 0 && (
        <BookingSection 
          professionalId={activeLinks[0].professional.id} 
          patientId={profile?.id}
          onBooked={load}
        />
      )}

      {/* Vínculo e Resumo */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <PendingRequestsSection requests={requests} />
          
          <section>
            <h2 className="text-lg font-bold tracking-tight mb-4 px-1" style={{fontFamily:"'Outfit',sans-serif"}}>
              Meu profissional
            </h2>
            {loading ? (
              <div className="ds-card p-8 text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
            ) : noLinkAtAll ? (
              <NoLinkCard onLinked={load} />
            ) : (
              <div className="space-y-4">
                {activeLinks.map(l => (
                  <LinkCard key={l.id} link={l} status="active" />
                ))}
                {pendingLinks.map(l => (
                  <LinkCard key={l.id} link={l} status="pending" />
                ))}
              </div>
            )}
          </section>

          <SessionsHistory appts={appts} onCancel={handleCancel} loading={loading} />
        </div>

        <div className="lg:col-span-4 space-y-6">
          <DashboardSummary links={links} appts={appts} />
          
          <div className="ds-clinical-card flex flex-col gap-4">
            <ShieldCheck className="w-8 h-8 text-[hsl(var(--brand))] shrink-0" />
            <div>
              <div className="ds-overline">Segurança</div>
              <p className="mt-1 text-[11px] text-[hsl(var(--text-secondary))] leading-relaxed">
                Seus dados são protegidos pela LGPD. Em caso de emergência, ligue para o CVV: <strong>188</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreditBalanceCard({ patientId, clinicId, professionalId, refreshKey }) {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) return;
    getPatientBalance(patientId, { clinic_id: clinicId, professional_id: professionalId })
      .then(setBalance)
      .finally(() => setLoading(false));
  }, [patientId, clinicId, professionalId, refreshKey]);

  if (loading) return <div className="ds-card p-6 animate-pulse bg-[hsl(var(--bg-base))] h-32"></div>;

  return (
    <div className="ds-card p-3 md:p-5 border-l-4 border-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/10 shadow-sm overflow-hidden relative group h-full">
      <div className="flex flex-col md:flex-row md:items-start justify-between relative z-10 gap-2">
        <div>
          <div className="text-[hsl(var(--brand))] text-[10px] uppercase font-bold tracking-[0.15em] mb-1 opacity-80">Saldo</div>
          <div className="text-3xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>{balance ?? 0}</div>
          <div className="text-[10px] text-[hsl(var(--text-secondary))] mt-1 font-medium">sessões</div>
        </div>
        <div className="p-2 md:p-3 bg-[hsl(var(--brand-light))]/40 rounded-xl group-hover:scale-110 transition-transform w-fit self-end md:self-auto">
          <Ticket className="w-5 h-5 md:w-8 md:h-8 text-[hsl(var(--brand))]" />
        </div>
      </div>
    </div>
  );
}

function PendingRequestsSection({ requests }) {
  const pending = useMemo(() => requests?.filter(r => r.status === 'pending') || [], [requests]);

  if (pending.length === 0) return null;

  return (
    <div className="ds-card p-4 bg-[hsl(var(--brand-light))]/5 border-[hsl(var(--brand))]/20 border-2">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-[hsl(var(--brand))] rounded-lg">
          <Hourglass className="w-5 h-5 text-white animate-pulse" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-[hsl(var(--brand))] uppercase tracking-wider mb-1">Atenção Necessária</h3>
          <p className="text-sm font-bold text-[hsl(var(--text-primary))] mb-3">Você possui solicitações de crédito aguardando aprovação.</p>
          
          <div className="space-y-2">
            {pending.map(r => (
              <div key={r.id} className="p-3 bg-white/50 rounded-xl border border-[hsl(var(--brand))]/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-[hsl(var(--brand))]">{r.plan?.name}</div>
                  <div className="text-[11px] text-[hsl(var(--text-secondary))]">Solicitado para: <span className="font-bold text-[hsl(var(--text-primary))]">{r.professional?.full_name}</span></div>
                </div>
                <div className="text-[9px] font-bold text-[hsl(var(--brand))] px-2 py-1 bg-[hsl(var(--brand-light))]/20 rounded-full">
                  AGUARDANDO APROVAÇÃO
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-[hsl(var(--brand))]/5 rounded-xl border border-dashed border-[hsl(var(--brand))]/30">
            <p className="text-[10px] text-[hsl(var(--text-secondary))] leading-relaxed">
              <strong>Nota:</strong> Seu pedido já foi enviado ao profissional. Caso tenha urgência ou alguma dúvida sobre o pagamento, por favor, entre em contato diretamente com o seu médico/terapeuta.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestPlanSection({ professionalId, clinicId, requests }) {
  const [plans, setPlans] = useState([]);
  const [profProfile, setProfProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);

  const hasPending = useMemo(() => {
    return requests?.some(r => r.status === 'pending' && r.professional_id === professionalId);
  }, [requests, professionalId]);

  useEffect(() => {
    listPlans().then(setPlans);
    if (professionalId) {
      getProfessionalProfile(professionalId).then(setProfProfile);
    }
  }, [professionalId]);

  const handleRequest = async (planId) => {
    if (!confirm("Deseja realmente solicitar este plano? O profissional receberá sua solicitação para aprovação.")) return;
    setBusy(true);
    try {
      await createPlanRequest(professionalId, planId, clinicId);
      setRequested(true);
      setTimeout(() => setRequested(false), 3000);
    } catch (e) {
      alert("Erro ao solicitar plano: " + e.message);
    } finally { setBusy(false); }
  };

  const sessionPrice = profProfile?.session_price || 150;

  const calculateTotal = (p) => {
    const baseTotal = p.credits_amount * sessionPrice;
    let discount = 0;
    
    if (p.credits_amount === 4) discount = profProfile?.discount_monthly ?? 5;
    else if (p.credits_amount === 12) discount = profProfile?.discount_trimestral ?? 10;
    else if (p.credits_amount === 24) discount = profProfile?.discount_semestral ?? 15;
    else discount = p.discount_percentage || 0;

    return baseTotal * (1 - (discount / 100));
  };

  return (
    <div className="ds-card p-3 md:p-5 h-full">
      <div className="ds-overline mb-2">Planos</div>
      {hasPending ? (
        <div className="p-4 bg-[hsl(var(--brand-light))]/10 border border-dashed border-[hsl(var(--brand))]/30 rounded-xl text-center">
          <Hourglass className="w-6 h-6 text-[hsl(var(--brand))] mx-auto mb-2 opacity-50" />
          <p className="text-[10px] font-bold text-[hsl(var(--brand))] uppercase tracking-wider">Solicitação Pendente</p>
          <p className="text-[9px] text-[hsl(var(--text-secondary))] mt-1">Aguarde a aprovação do profissional para solicitar novos créditos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {plans.map(p => {
            const total = calculateTotal(p);
            const hasDiscount = total < (p.credits_amount * sessionPrice);

            return (
              <button key={p.id} disabled={busy} onClick={() => handleRequest(p.id)}
                className="p-2 rounded-lg border border-[hsl(var(--border-default))] text-left hover:border-[hsl(var(--brand))] hover:bg-[hsl(var(--brand-light))]/5 transition-all group flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold text-[hsl(var(--brand))] uppercase tracking-wider">{p.name}</div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[hsl(var(--text-primary))]" style={{fontFamily: "'Outfit', sans-serif"}}>
                      R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-[9px] text-[hsl(var(--text-tertiary))] font-medium">({p.credits_amount} sessões)</span>
                  </div>
                  {hasDiscount && (
                    <div className="text-[8px] text-green-600 font-bold">
                      Economia de R$ {((p.credits_amount * sessionPrice) - total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
              </div>
              <ArrowRight className="w-3 h-3 text-[hsl(var(--text-tertiary))] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
            </button>
          );
        })}
        </div>
      )}
      {requested && <div className="mt-2 text-[9px] text-green-600 font-bold uppercase tracking-wider ds-fade-up">Enviado!</div>}
    </div>
  );
}

function BookingSection({ professionalId, patientId, onBooked }) {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSlots = async () => {
    setSlots([]); // Clear old slots while loading
    setLoading(true);
    try { setSlots(await getAvailableSlots(professionalId, date)); } 
    catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSlots(); }, [date, professionalId]);

  const handleBook = async (slot) => {
    if (!confirm(`Confirmar agendamento para ${formatDate(slot.start)} às ${formatTime(slot.start)}?`)) return;
    setBusy(true);
    try {
      await createBooking(professionalId, slot.start, slot.end);
      alert("Agendamento realizado com sucesso!");
      loadSlots();
      if (onBooked) onBooked();
    } catch (e) {
      alert(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="ds-card p-5 md:p-6 mb-8 border-t-4 border-[hsl(var(--brand))] shadow-sm overflow-hidden relative">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="ds-overline mb-0.5">Próxima Sessão</div>
          <h3 className="text-xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Agende seu horário</h3>
        </div>
        <div className="flex items-center gap-2 bg-[hsl(var(--bg-base))] p-1.5 rounded-xl border border-[hsl(var(--border-default))]">
          <label className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase ml-2">Data:</label>
          <input type="date" className="ds-input py-1 px-3 w-auto border-none bg-transparent shadow-none" value={date} min={new Date().toISOString().split("T")[0]} onChange={e => setDate(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center animate-pulse text-sm text-[hsl(var(--text-tertiary))] font-medium">Buscando horários disponíveis...</div>
      ) : slots.length === 0 ? (
        <div className="py-12 text-center bg-[hsl(var(--bg-base))] rounded-2xl border border-dashed border-[hsl(var(--border-default))]">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--text-tertiary))] opacity-20" />
          <p className="text-sm text-[hsl(var(--text-tertiary))] font-medium">Não há horários disponíveis para esta data.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {slots.map((s, i) => (
            <button key={i} disabled={busy} onClick={() => handleBook(s)}
              className="p-2.5 text-center rounded-xl border border-[hsl(var(--border-default))] hover:border-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))] hover:text-white transition-all group">
              <div className="text-xs font-bold group-hover:text-white">{formatTime(s.start)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LinkCard({ link, status }) {
  const p = link.professional;
  return (
    <div className="ds-card p-5 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`patient-link-${link.id}`}>
      <Avatar name={p?.full_name || "?"} size={52} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base font-bold truncate" style={{fontFamily:"'Outfit',sans-serif"}}>{p?.full_name || "—"}</span>
          <div className="hidden sm:block">
            {status === "active" ? (
              <span className="ds-badge ds-badge-success py-0.5">Ativo</span>
            ) : (
              <span className="ds-badge ds-badge-warning py-0.5">Pendente</span>
            )}
          </div>
        </div>
        <div className="text-[11px] text-[hsl(var(--text-tertiary))] font-medium flex items-center gap-2">
          <CalendarDays className="w-3 h-3" />
          <span>Solicitado em {formatDate(link.requested_at)}</span>
          {link.approved_at && (
            <>
              <span className="opacity-30">·</span>
              <span>Aprovado em {formatDate(link.approved_at)}</span>
            </>
          )}
        </div>
        <div className="sm:hidden mt-2">
          {status === "active" ? (
            <span className="ds-badge ds-badge-success text-[10px] py-0">Ativo</span>
          ) : (
            <span className="ds-badge ds-badge-warning text-[10px] py-0">Pendente</span>
          )}
        </div>
      </div>
    </div>
  );
}


function NoLinkCard({ onLinked }) {
  const [mode, setMode] = useState("invite");
  const [code, setCode] = useState("");
  const [q, setQ] = useState("");
  const [pros, setPros] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (mode !== "search") return;
    const t = setTimeout(() => listPublicProfessionals(q).then(setPros).catch(() => setPros([])), 250);
    return () => clearTimeout(t);
  }, [mode, q]);

  const useCode = async () => {
    setMsg(""); setBusy(true);
    try {
      await redeemInviteCode(code.trim().toUpperCase());
      setMsg("Vinculado com sucesso!");
      onLinked();
    } catch (e) { setMsg(e.message || "Erro ao vincular."); }
    finally { setBusy(false); }
  };

  const askLink = async (proId) => {
    setMsg(""); setBusy(true);
    try {
      await requestLink(proId);
      setMsg("Solicitação enviada. Aguarde a aprovação.");
      onLinked();
    } catch (e) { setMsg(e.message || "Erro ao solicitar."); }
    finally { setBusy(false); }
  };

  return (
    <div className="ds-card p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] flex items-center justify-center"><Heart className="w-5 h-5" /></div>
        <div>
          <h3 className="text-base font-semibold" style={{fontFamily:"'Outfit',sans-serif"}}>Vincule-se a um profissional</h3>
          <p className="text-sm text-[hsl(var(--text-secondary))]">Use um código de convite ou escolha da lista pública.</p>
        </div>
      </div>

      <div className="flex p-1 rounded-lg bg-[hsl(var(--bg-base))] border border-[hsl(var(--border-default))] mb-4">
        <button data-testid="link-mode-invite" onClick={() => setMode("invite")} className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-md text-sm font-medium transition-colors ${mode === "invite" ? "bg-[hsl(var(--brand))] text-white" : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--bg-hover))]"}`}>
          <Ticket className="w-4 h-4" /> Tenho código
        </button>
        <button data-testid="link-mode-search" onClick={() => setMode("search")} className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-md text-sm font-medium transition-colors ${mode === "search" ? "bg-[hsl(var(--brand))] text-white" : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--bg-hover))]"}`}>
          <Search className="w-4 h-4" /> Buscar profissional
        </button>
      </div>

      {mode === "invite" ? (
        <div className="flex gap-2">
          <input data-testid="link-code-input" className="ds-input uppercase tracking-wider" placeholder="ABC-1234" value={code} onChange={e => setCode(e.target.value.toUpperCase())} />
          <button data-testid="link-code-submit" disabled={busy || !code} onClick={useCode} className="ds-btn-primary">
            Vincular <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]" />
            <input data-testid="link-search-input" className="ds-input pl-9" placeholder="Buscar pelo nome..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <div className="max-h-72 overflow-y-auto space-y-2">
            {pros.length === 0 ? (
              <div className="text-sm text-[hsl(var(--text-tertiary))] py-4 text-center">Nenhum profissional encontrado.</div>
            ) : pros.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border-default))]">
                <Avatar name={p.full_name} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.full_name}</div>
                  <div className="text-xs text-[hsl(var(--text-tertiary))] truncate">{p.crp ? `CRP ${p.crp}` : "Psicólogo(a)"}</div>
                </div>
                <button data-testid={`link-request-${p.id}`} disabled={busy} onClick={() => askLink(p.id)} className="ds-btn-primary h-9 px-3">
                  Solicitar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {msg && <div className="mt-3 text-sm text-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/50 rounded-md px-3 py-2">{msg}</div>}
    </div>
  );
}

function SessionsHistory({ appts, onCancel, loading }) {
  if (loading) return <div className="ds-card p-6 animate-pulse bg-[hsl(var(--bg-base))] h-40"></div>;
  
  return (
    <section>
      <h2 className="text-lg font-bold tracking-tight mb-4 px-1" style={{fontFamily:"'Outfit',sans-serif"}}>
        Minhas consultas
      </h2>
      {appts.length === 0 ? (
        <div className="ds-card p-6 text-sm text-[hsl(var(--text-tertiary))] text-center">Sem agendamentos no momento.</div>
      ) : (
        <div className="space-y-3">
          {appts.slice(0, 5).map(a => (
            <div key={a.id} className="ds-card p-4 hover:border-[hsl(var(--brand))] transition-colors group">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-[hsl(var(--brand))]" style={{fontFamily: "'Outfit',sans-serif"}}>
                  {formatDate(a.start_at)} às {formatTime(a.start_at)}
                </div>
                <AppointmentStatusBadge status={a.status} />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-[hsl(var(--text-secondary))] flex items-center gap-1.5 font-medium">
                  {a.modality === "online" ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
                  {a.modality === "online" ? "Online" : "Presencial"} · {a.type}
                </div>
                {new Date(a.start_at) - new Date() > 24 * 60 * 60 * 1000 && (
                  <button 
                    onClick={() => onCancel(a.id)}
                    className="text-[10px] font-bold text-[hsl(var(--danger-text))] hover:underline uppercase tracking-wider"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          ))}
          {appts.length > 5 && (
            <Link to="/portal/agenda" className="block text-center text-xs font-bold text-[hsl(var(--brand))] hover:underline pt-2">
              Ver todos os agendamentos
            </Link>
          )}
        </div>
      )}
    </section>
  );
}

function DashboardSummary({ links, appts }) {
  const nextAppt = useMemo(() => {
    return appts.find(a => new Date(a.start_at) > new Date());
  }, [appts]);

  return (
    <div className="space-y-6">
      {nextAppt && (
        <div className="ds-card p-5 bg-[hsl(var(--brand))]/5 border-[hsl(var(--brand))]/20">
          <div className="text-[10px] font-bold text-[hsl(var(--brand))] uppercase tracking-widest mb-3">Próxima Consulta</div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white text-[hsl(var(--brand))] flex flex-col items-center justify-center shadow-sm border border-[hsl(var(--brand))]/10">
              <div className="text-[10px] font-bold uppercase">{new Date(nextAppt.start_at).toLocaleString('pt-BR', { month: 'short' }).replace('.', '')}</div>
              <div className="text-xl font-black leading-none">{new Date(nextAppt.start_at).getDate()}</div>
            </div>
            <div>
              <div className="text-sm font-bold text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>
                {formatTime(nextAppt.start_at)}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-secondary))] font-medium mt-0.5">
                {nextAppt.type} · {nextAppt.modality === 'online' ? 'Online' : 'Presencial'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="ds-card p-5">
        <div className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest mb-4">Acesso Rápido</div>
        <div className="grid grid-cols-1 gap-2">
          <Link to="/portal/agenda" className="flex items-center justify-between p-3 rounded-xl bg-[hsl(var(--bg-base))] hover:bg-[hsl(var(--brand-light))]/10 transition-colors group">
            <div className="flex items-center gap-3">
              <CalendarDays className="w-4 h-4 text-[hsl(var(--brand))]" />
              <span className="text-xs font-bold">Minha Agenda</span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-[hsl(var(--text-tertiary))] group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/portal/sessoes" className="flex items-center justify-between p-3 rounded-xl bg-[hsl(var(--bg-base))] hover:bg-[hsl(var(--brand-light))]/10 transition-colors group">
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-[hsl(var(--brand))]" />
              <span className="text-xs font-bold">Minhas Sessões</span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-[hsl(var(--text-tertiary))] group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export function PatientAppointments() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    listAppointments()
      .then(setList)
      .catch(err => {
        console.error("Erro ao carregar agenda:", err);
        setError(err.message || "Erro desconhecido ao carregar agenda");
      })
      .finally(() => setLoading(false));
  }, []);

  if (error) return <div className="ds-card p-10 text-center text-[hsl(var(--danger-text))]">{error}</div>;
  return (
    <div className="ds-fade-up">
      <PageHeader eyebrow="Minhas consultas" title="Agenda" description="Próximas e passadas — todas reunidas em um lugar." />
      {loading ? <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div> :
        list.length === 0 ? <EmptyState icon={CalendarDays} title="Nenhuma consulta" description="Quando seu profissional agendar, aparecerá aqui." /> :
        <>
          {/* Mobile View: Cards */}
          <div className="grid grid-cols-1 gap-4 md:hidden" data-testid="patient-appts-cards">
            {list.map(a => (
              <div key={a.id} className="ds-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] flex items-center justify-center">
                      <CalendarDays className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm font-bold" style={{fontFamily:"'Outfit',sans-serif"}}>{formatDate(a.start_at)}</div>
                      <div className="text-xs text-[hsl(var(--text-tertiary))] font-medium">{formatTime(a.start_at)}</div>
                    </div>
                  </div>
                  <AppointmentStatusBadge status={a.status} />
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-[hsl(var(--border-divider))]">
                  <div className="text-xs font-semibold text-[hsl(var(--text-secondary))]">
                    {a.type} · {a.modality === "online" ? "Online" : "Presencial"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View: List */}
          <ul className="hidden md:block ds-card divide-y divide-[hsl(var(--border-divider))]" data-testid="patient-appts-list">
            {list.map(a => (
              <li key={a.id} className="px-4 py-4 flex items-center gap-4">
                <div className="w-12 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-tertiary))]">{formatDate(a.start_at)}</div>
                  <div className="text-base font-semibold" style={{fontFamily:"'Outfit',sans-serif"}}>{formatTime(a.start_at)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.type} · {a.modality === "online" ? "Online" : "Presencial"}</div>
                  <div className="text-xs text-[hsl(var(--text-tertiary))]">{formatDateTime(a.start_at)}</div>
                </div>
                <AppointmentStatusBadge status={a.status} />
              </li>
            ))}
          </ul>
        </>
      }
    </div>
  );
}

export function PatientSessions() {
  const { profile } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      listPatientSessions(profile.id).then(setList).finally(() => setLoading(false));
    }
  }, [profile?.id]);

  return (
    <div className="ds-fade-up">
      <PageHeader 
        eyebrow="Histórico clínico" 
        title="Minhas sessões" 
        description="Acompanhe as orientações e o plano terapêutico compartilhado pelo seu profissional." 
      />
      
      {loading ? (
        <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
      ) : list.length === 0 ? (
        <EmptyState 
          icon={Clock} 
          title="Nenhuma sessão registrada" 
          description="Seu histórico de sessões e planos terapêuticos aparecerá aqui assim que seu profissional realizar o primeiro registro." 
        />
      ) : (
        <div className="space-y-4">
          <div className="ds-clinical-card mb-6">
            <div className="flex items-center gap-3 text-[hsl(var(--brand))] mb-2">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-wider">Aviso de Privacidade</span>
            </div>
            <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed">
              Para sua privacidade e segurança, apenas a parte do <strong>Plano Terapêutico</strong> e orientações são compartilhadas aqui. 
              As anotações clínicas detalhadas permanecem restritas ao seu profissional.
            </p>
          </div>

          <ol className="relative ml-4 border-l-2 border-[hsl(var(--border-default))] space-y-6">
            {list.map((s, i) => (
              <li key={s.id} className="pl-6 relative ds-fade-up" style={{animationDelay: `${i * 60}ms`}}>
                <span className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-white border-2 border-[hsl(var(--brand))]" />
                <div className="ds-card p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarDays className="w-4 h-4 text-[hsl(var(--brand))]" />
                    <span className="text-sm font-semibold">{formatDate(s.session_date)}</span>
                    <span className="text-xs text-[hsl(var(--text-tertiary))]">· Sessão finalizada</span>
                  </div>
                  
                  <div className="border-l-4 border-[hsl(var(--brand-light))] pl-4 py-1">
                    <div className="text-[10px] font-bold text-[hsl(var(--brand))] uppercase tracking-widest mb-1.5">Plano Terapêutico & Orientações</div>
                    <div className="text-sm text-[hsl(var(--text-primary))] whitespace-pre-wrap leading-relaxed italic">
                      {s.plan || "Nenhuma orientação específica registrada para esta sessão."}
                    </div>
                  </div>

                  {s.techniques?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {s.techniques.map(t => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-[hsl(var(--brand-light))]/30 text-[hsl(var(--brand))] text-[10px] font-medium border border-[hsl(var(--brand-light))]">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
